import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Users, Plus, Minus } from 'lucide-react';
import { BRAND_PRIMARY_BTN_CLASS } from '@/constants/brandColors';
import { cn } from '@/lib/utils';
import { SEATED_INFANT, isSeatedInfant } from '@/utils/passengerTypes';

/**
 * `onOpenChange(open)` tells the form when the picker opens and closes, so its
 * early search can wait for the party to be settled (useSearchPickers).
 */
export default function PassengerSelector({ passengers = [{ type: 'adult' }], onChange, onOpenChange, className, disabled = false }) {
  const [isOpen, setOpenState] = useState(false);
  const setIsOpen = (open) => {
    setOpenState(open);
    onOpenChange?.(open);
  };
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Convert passengers array to counts
  const getCounts = () => {
    const counts = { adults: 0, children: 0, infantsOnLap: 0, infantsOnSeat: 0 };
    passengers.forEach(passenger => {
      if (passenger.type === 'adult') counts.adults++;
      else if (passenger.type === 'child') counts.children++;
      else if (passenger.type === 'infant_without_seat') counts.infantsOnLap++;
      else if (isSeatedInfant(passenger.type)) counts.infantsOnSeat++;
    });
    return counts;
  };

  const counts = getCounts();
  const totalPassengers = Object.values(counts).reduce((a, b) => a + b, 0);

  const handleCountChange = (type, delta) => {
    const newCounts = { ...counts };
    newCounts[type] = Math.max(0, newCounts[type] + delta);

    // Rules
    const newTotal = Object.values(newCounts).reduce((a, b) => a + b, 0);
    if (newTotal > 9) return;
    if (newTotal === 0) return;
    if (newCounts.adults === 0 && (newCounts.children > 0 || newCounts.infantsOnLap > 0 || newCounts.infantsOnSeat > 0)) return;
    // One infant per adult's lap (airline rule; Google won't price more).
    if (newCounts.infantsOnLap > newCounts.adults) return;

    // Convert back to array
    const newPassengers = [];
    for (let i = 0; i < newCounts.adults; i++) newPassengers.push({ type: 'adult' });
    for (let i = 0; i < newCounts.children; i++) newPassengers.push({ type: 'child' });
    for (let i = 0; i < newCounts.infantsOnLap; i++) newPassengers.push({ type: 'infant_without_seat' });
    for (let i = 0; i < newCounts.infantsOnSeat; i++) newPassengers.push({ type: SEATED_INFANT });

    onChange(newPassengers);
  };

  const getDisplayText = () => {
    if (totalPassengers === 0) return isMobile ? '1' : '1 adult';
    
    // On mobile, show just the number
    if (isMobile) return totalPassengers.toString();
    
    // On desktop, show full text
    const parts = [];
    if (counts.adults > 0) parts.push(`${counts.adults} adult${counts.adults > 1 ? 's' : ''}`);
    if (counts.children > 0) parts.push(`${counts.children} child${counts.children > 1 ? 'ren' : ''}`);
    if (counts.infantsOnLap > 0) parts.push(`${counts.infantsOnLap} infant${counts.infantsOnLap > 1 ? 's' : ''} on lap`);
    if (counts.infantsOnSeat > 0) parts.push(`${counts.infantsOnSeat} infant${counts.infantsOnSeat > 1 ? 's' : ''} on seat`);
    
    const text = parts.join(', ');
    return text.length > 25 ? `${totalPassengers} passenger${totalPassengers > 1 ? 's' : ''}` : text;
  };

  return (
    <div className="relative w-full">
      <Popover open={disabled ? false : isOpen} onOpenChange={(open) => !disabled && setIsOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            disabled={disabled}
            className={`w-full justify-start text-left text-base md:text-sm font-normal ${className} pl-9 max-[374px]:pl-8 truncate ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <Users className="absolute left-3 max-[374px]:left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-current opacity-80" />
            {getDisplayText()}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 rounded-2xl bg-brand-950/95 border-brand-800/45 text-white p-4 backdrop-blur-xl shadow-2xl" align="center" side="bottom" sideOffset={8}>
          <div className="space-y-1">
            {/* Adults */}
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="text-white font-medium text-sm">Adults</div>
                <div className="text-white/45 text-xs">Age 18+</div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCountChange('adults', -1)}
                  disabled={counts.adults <= 1}
                  className="h-8 w-8 rounded-full bg-white/[0.04] border-white/10 text-white/70 hover:bg-white/[0.09] hover:text-white hover:border-white/20 transition-colors disabled:opacity-30"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-white font-medium w-6 text-center tabular-nums">{counts.adults}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCountChange('adults', 1)}
                  disabled={totalPassengers >= 9}
                  className="h-8 w-8 rounded-full bg-white/[0.04] border-white/10 text-white/70 hover:bg-white/[0.09] hover:text-white hover:border-white/20 transition-colors disabled:opacity-30"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="border-t border-white/10" />
            
            {/* Children */}
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="text-white font-medium text-sm">Children</div>
                <div className="text-white/45 text-xs">Age 2-17</div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCountChange('children', -1)}
                  disabled={counts.children === 0}
                  className="h-8 w-8 rounded-full bg-white/[0.04] border-white/10 text-white/70 hover:bg-white/[0.09] hover:text-white hover:border-white/20 transition-colors disabled:opacity-30"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-white font-medium w-6 text-center tabular-nums">{counts.children}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCountChange('children', 1)}
                  disabled={totalPassengers >= 9}
                  className="h-8 w-8 rounded-full bg-white/[0.04] border-white/10 text-white/70 hover:bg-white/[0.09] hover:text-white hover:border-white/20 transition-colors disabled:opacity-30"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="border-t border-white/10" />
            
            {/* Infants on lap */}
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="text-white font-medium text-sm">Infants on lap</div>
                <div className="text-white/45 text-xs">Under 2, no seat</div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCountChange('infantsOnLap', -1)}
                  disabled={counts.infantsOnLap === 0}
                  className="h-8 w-8 rounded-full bg-white/[0.04] border-white/10 text-white/70 hover:bg-white/[0.09] hover:text-white hover:border-white/20 transition-colors disabled:opacity-30"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-white font-medium w-6 text-center tabular-nums">{counts.infantsOnLap}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCountChange('infantsOnLap', 1)}
                  disabled={totalPassengers >= 9 || counts.infantsOnLap >= counts.adults}
                  className="h-8 w-8 rounded-full bg-white/[0.04] border-white/10 text-white/70 hover:bg-white/[0.09] hover:text-white hover:border-white/20 transition-colors disabled:opacity-30"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="border-t border-white/10" />
            
            {/* Infants on seat */}
            <div className="flex items-center justify-between py-3">
              <div>
                <div className="text-white font-medium text-sm">Infants on seat</div>
                <div className="text-white/45 text-xs">Under 2, with seat</div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCountChange('infantsOnSeat', -1)}
                  disabled={counts.infantsOnSeat === 0}
                  className="h-8 w-8 rounded-full bg-white/[0.04] border-white/10 text-white/70 hover:bg-white/[0.09] hover:text-white hover:border-white/20 transition-colors disabled:opacity-30"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-white font-medium w-6 text-center tabular-nums">{counts.infantsOnSeat}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCountChange('infantsOnSeat', 1)}
                  disabled={totalPassengers >= 9}
                  className="h-8 w-8 rounded-full bg-white/[0.04] border-white/10 text-white/70 hover:bg-white/[0.09] hover:text-white hover:border-white/20 transition-colors disabled:opacity-30"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          
          <div className="mt-4 pt-3 border-t border-white/10">
            <Button
              type="button"
              onClick={() => setIsOpen(false)}
              className={cn(BRAND_PRIMARY_BTN_CLASS, 'w-full h-11 rounded-full')}
            >
              Done
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}